import * as _ from "lodash";
import * as Redux from "redux";
import * as semver from "semver";
import { ThunkResult, Utilities } from "../../shared/utilities";
import { AgentGroupLogLevelSetting, LogLevel } from "../model/LogLevelSetting";

import { State } from "../model/State";
import { fetchFinish, FetchId, fetchLogLevelUsageData, fetchLogRetentionUsageData, fetchStart, reportFetchError, shouldFetchLogLevelUsageData, shouldFetchLogRetentionUsageData } from "./FetchActions";
import { deselectAllProcesses } from "./SelectProcessActions";

export function showLogLevelSettings(processIds?: number[]): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            dispatch(fetchStart(FetchId.openLogSettings));
            const state = getState();
            const selectedAgentGroup = _.values(state.agentGroups).find((a) => a.name === state.currentAgentGroupName);
            let processGuids: string[] | null = null;
            // This is left null if environment level log settings are being edited
            let selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging: boolean | null | undefined = null;
            if (processIds && processIds.length > 0) {
                const selectedProcesses = state.processes.filter((p) => processIds.indexOf(p.id) !== -1);
                processGuids = selectedProcesses.map((p) => p.uniqueIdentifier);
                const frendsVersions = selectedProcesses.map((p) => semver.coerce(p.frendsVersion));
                selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging =
                    frendsVersions.every((v) => v && (v.major > 5 || (v.major === 5 && v.minor >= 4)));
            }
            const response = await Utilities.postJsonAndParseResponse<AgentGroupLogLevelSetting>(`api/processList/logLevel/get/${selectedAgentGroup?.environment}/${state.currentAgentGroupName}`, processGuids);

            dispatch<IShowLogLevelSettingsAction>({
                type: SHOW_LOG_LEVEL_SETTINGS,
                settings: response,
                environmentName: selectedAgentGroup?.environment,
                allowLogEverything: selectedAgentGroup?.allowLogEverything,
                processIds: processIds,
                selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging: selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging
            });
            dispatch(fetchFinish(FetchId.openLogSettings));
        } catch (error) {
            reportFetchError(error);
        }
    };
}
export const SHOW_LOG_LEVEL_SETTINGS = "SHOW_LOG_LEVEL_SETTINGS";
export interface IShowLogLevelSettingsAction extends Redux.Action {
    settings: AgentGroupLogLevelSetting;
    environmentName: string | undefined;
    processIds?: number[];
    allowLogEverything: boolean | undefined;
    selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging?: boolean | null;
}

export const CLOSE_LOG_LEVEL_SETTINGS = "CLOSE_LOG_LEVEL_SETTINGS";
export function closeLogLevelSettings(): Redux.Action {
    return { type: CLOSE_LOG_LEVEL_SETTINGS };
}

export const SET_LOG_LEVEL = "SET_LOG_LEVEL";
export function setLogLevel(logLevel: LogLevel): ISetLogLevelAction {
    return { logLevel: logLevel, type: SET_LOG_LEVEL };
}
export interface ISetLogLevelAction extends Redux.Action {
    logLevel: LogLevel;
}

export const DEBUG_EXPIRES_IN = "DEBUG_EXPIRES_IN";
export function setDebugExpiresIn(expiresIn: number | null): IDebugExpiresAtAction {
    return { type: DEBUG_EXPIRES_IN, expiresIn: expiresIn };
}
export interface IDebugExpiresAtAction extends Redux.Action {
    expiresIn: number | null;
}

export const SET_RETENTION_DAYS = "SET_RETENTION_DAYS";
export function setRetentionDays(days?: number): ISetRetentionDaysAction {
    return { type: SET_RETENTION_DAYS, days: days };
}
export interface ISetRetentionDaysAction extends Redux.Action {
    days?: number;
}

export const SET_LOG_REQUEST_RESPONSE = "SET_LOG_REQUEST_RESPONSE";
export function setLogRequestResponse(log?: boolean): ISetLogReqestResponseAction {
    return {
        type: SET_LOG_REQUEST_RESPONSE,
        logRequestResponse: log
    };
}
export interface ISetLogReqestResponseAction extends Redux.Action {
    logRequestResponse?: boolean;
}

export function saveLogLevelSettings(): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            dispatch(fetchStart(FetchId.saveLogSettings));
            const state = getState();
            if (_.isEmpty(state.logLevelDialogState.processIds)) {
                await Utilities.postJsonAndParseResponse(`api/processList/logLevelForAgentGroup/set/${state.logLevelDialogState.environmentName}/${state.currentAgentGroupName}`,
                    {
                        historyStorageDays: state.logLevelDialogState.currentSettings?.historyStorageDays,
                        logTriggerRequestAndResponse: state.logLevelDialogState.currentSettings?.logTriggerRequestAndResponse,
                        logLevel: state.logLevelDialogState.currentSettings?.logLevel,
                        debugExpiresInSeconds: state.logLevelDialogState.currentSettings?.setDebugeExpiresInSeconds,
                        debugExpiresAt: state.logLevelDialogState.currentSettings?.debugExpiresAt
                    });
            } else {
                await Utilities.postJsonAndParseResponse(`api/processList/logLevelForProcesses/set/${state.logLevelDialogState.environmentName}/${state.currentAgentGroupName}`,
                    {
                        processIds: state.logLevelDialogState.processIds,
                        historyStorageDays: state.logLevelDialogState.currentSettings?.historyStorageDays,
                        logTriggerRequestAndResponse: state.logLevelDialogState.currentSettings?.logTriggerRequestAndResponse,
                        logLevel: state.logLevelDialogState.currentSettings?.logLevel,
                        debugExpiresInSeconds: state.logLevelDialogState.currentSettings?.setDebugeExpiresInSeconds,
                        debugExpiresAt: state.logLevelDialogState.currentSettings?.debugExpiresAt
                    });
            }
            Utilities.toastSuccess(FRENDS.Localizations.Successful);
            dispatch(fetchFinish(FetchId.saveLogSettings));
            dispatch(closeLogLevelSettings());
            dispatch(deselectAllProcesses());

            const environmentName = state.agentGroups[state.currentAgentGroupName].environment;
            if (shouldFetchLogLevelUsageData()) {
                dispatch(fetchLogLevelUsageData(environmentName, state.currentAgentGroupName));
            }

            if (shouldFetchLogRetentionUsageData()) {
                dispatch(fetchLogRetentionUsageData(environmentName, state.currentAgentGroupName));
            }

        } catch (error) {
            return dispatch(reportFetchError(error));
        }
    };
}
